import { readWeights } from "../index.js";
import { newID } from "../models.js";
import { isSessionReference, mendLayout, stripListMarker } from "../ruleParser.js";
import { looksLikeLabel } from "../trainingWeek.js";
import { renderItem, renderRows } from "./render.js";
import { runWords } from "./run.js";
import { RowsError, validRows } from "./valid.js";
import { readsSame } from "./verify.js";
import type { Amount, AmountUnit, Count, Effort, EffortWord, Hurdles, Item, ItemKind, Load, Rest, RestValue, Row } from "./types.js";

/**
 * His lines as rows (DESIGN.md §25.3); a line that does not fit is kept `written`. Lines the PDF wrapped
 * are joined first, as the reader joins them. Whatever the rows say must read as his text does: when
 * the whole doesn't (a layout the rows can't hold), every line is kept as he wrote it.
 */
export function liftLines(given: string[]): Row[] {
  // A sheet pasted from Windows or a spreadsheet brings \r, tabs and control characters, which no row may
  // hold (`validRows`): they go first, a tab as the two spaces the Text view shows it as.
  const lines = given.map((line) => line.replace(/\r/g, "").replace(/\t/g, "  ").replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, " "));
  const rows = liftMended(mendLayout(lines));
  // Rows the form couldn't save (a count of nothing, or past the form's limits) are never offered.
  if (readsSame(lines, renderRows(rows)) && savable(rows)) return rows;
  // Blank lines too: in his text a blank line ends a round.
  const kept = lines.slice(lines.findIndex((l) => l.trim() !== ""));
  while (kept.length > 0 && kept[kept.length - 1]!.trim() === "") kept.pop();
  return kept.map((text) => ({ type: "written", id: newID(), text: text.trim() === "" ? "" : text }));
}

function savable(rows: Row[]): boolean {
  try {
    validRows(rows);
    return true;
  } catch (error) {
    if (error instanceof RowsError) return false;
    throw error;
  }
}

function liftMended(lines: string[]): Row[] {
  const reading = readWeights(lines.join("\n")).lines;
  const rows: Row[] = [];
  let open: { blockId: string; row: Extract<Row, { type: "rounds" }>; indent: number } | null = null;
  let last: { item: Item; indent: number } | null = null;

  const push = (row: Row) => {
    rows.push(row);
    open = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]!;
    if (raw.trim() === "") {
      open = null;
      last = null;
      continue;
    }
    const line = reading[i];
    const indent = indentOf(raw);
    const body = stripListMarker(raw.trim()).body.trim();
    const kind = line?.kind ?? "unread";

    const section = sectionOf(body);
    if (section !== null) {
      push({ type: "section", id: newID(), name: section });
      last = null;
      continue;
    }

    if (kind === "block") {
      const head = roundsHeader(body);
      // Words after the header that aren't an exercise would be lost: such a line stays as he wrote it.
      const first = head?.first !== undefined ? verified(head.first) : undefined;
      if (head !== null && first !== null) {
        const row: Extract<Row, { type: "rounds" }> = { type: "rounds", id: newID(), sets: head.sets, style: "list", items: [] };
        if (head.effort !== undefined) row.effort = head.effort;
        if (head.note !== undefined) row.note = head.note;
        rows.push(row);
        open = { blockId: line!.blockId!, row, indent };
        last = null;
        if (first !== undefined) {
          row.items.push(first);
          last = { item: first, indent: indent + 2 };
        }
        continue;
      }
    }

    // A line indented under an item that says no work of its own is that item's note.
    if (last !== null && indent > last.indent && !startsWithCount(body) && (kind === "note" || kind === "unread")) {
      (last.item.subNotes ??= []).push(raw.trim());
      continue;
    }

    if (kind === "exercise" && line!.exerciseIds.length > 1) {
      const inline = inlineRounds(body);
      if (inline !== null && !(open !== null && line!.blockId === open.blockId) && readsSame([body], renderRows([inline]))) {
        push(inline);
        last = null;
        continue;
      }
    }

    if (kind === "heading" && isReference(body)) {
      push({ type: "reference", id: newID(), label: body });
      last = null;
      continue;
    }

    if (kind !== "exercise") {
      const rest = restLine(body);
      if (rest !== null) {
        push({ type: "rest", id: newID(), seconds: rest });
        last = null;
        continue;
      }
    }

    if (kind === "exercise" || kind === "note" || kind === "unread" || kind === "heading") {
      const member = open !== null && line?.blockId === open.blockId && kind === "exercise";
      const item = kind === "exercise" || startsWithCount(body) || /^(dec|hep|deca|hepta)\s*:/i.test(body) ? verified(body) : null;
      if (item !== null && (kind === "exercise" || item.amount !== undefined)) {
        if (member) {
          open!.row.items.push(item);
        } else {
          push({ type: "item", id: newID(), item });
        }
        last = { item, indent };
        continue;
      }
      const rest = restLine(body);
      if (rest !== null) {
        push({ type: "rest", id: newID(), seconds: rest });
        last = null;
        continue;
      }
      if (isReference(body)) {
        push({ type: "reference", id: newID(), label: body });
        last = null;
        continue;
      }
      if (!startsWithCount(body)) {
        push({ type: "instruction", id: newID(), text: body });
        last = null;
        continue;
      }
    }
    push({ type: "written", id: newID(), text: raw });
    last = null;
  }
  return rows;
}

/** The line as an item only when the item's own text gives a phone what his line gives it. */
function verified(body: string): Item | null {
  for (const keepRunWords of [false, true]) {
    const item = parseItem(body, { keepRunWords });
    if (item !== null && readsSame([body], [renderItem(item)])) return item;
  }
  return null;
}

function indentOf(raw: string): number {
  return (/^[ \t]*/.exec(raw)?.[0] ?? "").replace(/\t/g, "  ").length;
}

function sectionOf(body: string): "Prep" | "Main" | null {
  if (/^(prep|warm[\s-]?up)\s*:$/i.test(body)) return "Prep";
  if (/^(main|main set|workout|strength)\s*:?$/i.test(body)) return "Main";
  return null;
}

function isReference(body: string): boolean {
  return looksLikeLabel(body) || isSessionReference(body);
}

function startsWithCount(body: string): boolean {
  return /^\d/.test(body);
}

/** `4 sets`, `3 sets (light)`, `4 sets of`, `2-3 Sets:`, `2-3 Sets: 10 V-Crunchs`, `3 rounds`. */
/**
 * A round typed on its own, `4 sets (medium) - keep moving`, as the form starts one (DESIGN.md §25.5):
 * new writing, not his text being read, so it isn't held to the reader.
 */
export function roundsRow(text: string): Row | null {
  const head = roundsHeader(stripListMarker(text.trim()).body.trim());
  if (head === null || head.first !== undefined) return null;
  const row: Extract<Row, { type: "rounds" }> = { type: "rounds", id: newID(), sets: head.sets, style: "list", items: [] };
  if (head.effort !== undefined) row.effort = head.effort;
  if (head.note !== undefined) row.note = head.note;
  return row;
}

function roundsHeader(body: string): { sets: Count; effort?: Effort; note?: string; first?: string } | null {
  const dashed = /^(.*?\b(?:sets?|rounds?)(?:\s+of)?\s*(?:\([^()]*\))?)\s+[-–—]\s+(.+)$/i.exec(body);
  const match = /^(\d+)(?:\s*-\s*(\d+))?\s*(?:sets?|rounds?)(?:\s+of)?\s*(?:\((.*)\))?\s*:?\s*(.*)$/i.exec(dashed?.[1] ?? body);
  if (match === null) return null;
  const head: { sets: Count; effort?: Effort; note?: string; first?: string } = { sets: count(match[1]!, match[2]) };
  if (match[3] !== undefined && match[3].trim() !== "") {
    const parts = splitComma(match[3]);
    const range = /^(\d+)\s*-\s*(\d+)(?:\s*sets?)?$/i.exec(parts[parts.length - 1]!);
    if (range !== null && match[2] === undefined && Number(range[2]) === head.sets.n) {
      head.sets = count(range[1]!, range[2]);
      parts.pop();
    }
    const word = parts.length > 0 ? effortWord(parts[0]!) : null;
    if (word !== null) {
      head.effort = { word };
      parts.shift();
    }
    if (parts.length > 0) head.note = parts.join(", ");
  }
  if (dashed !== null) head.note = [head.note, dashed[2]!.trim()].filter((n) => n !== undefined && n !== "").join(", ");
  if (match[4] !== undefined && match[4].trim() !== "") {
    if (match[3] === undefined && /^\(/.test(match[4])) return null;
    head.first = match[4].trim();
  }
  return head;
}

/** `5 sets (3 bench-press (heavy) + 6 V-crunch)`, `4x(3 deadlift (heavy) + 5 box hops)`. */
function inlineRounds(body: string): Row | null {
  const match = /^(\d+)(?:\s*-\s*(\d+))?\s*(?:x|sets?(?:\s+of)?|rounds?)\s*\((.*)\)\s*$/i.exec(body);
  if (match === null) return null;
  const parts = splitTop(match[3]!, "+");
  if (parts.length < 2) return null;
  const items = parts.map((p) => verified(p.trim()) ?? parseItem(p.trim()));
  if (items.some((i) => i === null)) return null;
  return { type: "rounds", id: newID(), sets: count(match[1]!, match[2]), style: "inline", items: items as Item[] };
}

function restLine(body: string): Count | null {
  const match = /^(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?\s*(min|mins|minutes?|s|sec|secs|seconds?)\s+(rest|break)$/i.exec(body);
  if (match === null) return null;
  const scale = /^m/i.test(match[3]!) ? 60 : 1;
  return scaled(count(match[1]!, match[2]), scale);
}

// MARK: One item

/** One line of his notation as an item, or null when it names nothing. */
export function parseItem(input: string, options: { keepRunWords?: boolean } = {}): Item | null {
  let text = input.trim();
  const item: Item = { id: newID(), kind: "lift", name: "" };

  const only = /^(Dec|Hep|Deca|Hepta|Decathlon|Heptathlon)\s*:\s*/i.exec(text);
  if (only !== null) {
    item.onlyFor = only[1]!;
    text = text.slice(only[0].length);
  }

  const counted = leadingCount(text);
  if (counted === null) return null;
  Object.assign(item, counted.fields);
  text = counted.rest;

  if (/\*/.test(withoutBrackets(text))) {
    item.perSide = true;
    text = starsOutside(text);
  }
  // The rest written after `R:` (with or without a dash), then the dashes, then the brackets of what is left.
  let restWords: string | null = null;
  const restCues: string[] = [];
  const r = /(?:^|\s)(?:with\s+)?-?\s*R\s*:\s*(.+?)(?=\s+[-–—]\s+|$)/.exec(text) ?? /(?:^|\s)-\s*Rest\s+(\d.*?)(?=\s+[-–—]\s+|$)/i.exec(text);
  if (r !== null) {
    const taken = takeBrackets(r[1]!);
    restWords = taken.rest.trim();
    restCues.push(...taken.groups);
    text = (text.slice(0, r.index) + text.slice(r.index + r[0].length)).trim();
  }
  const segments = splitTopDashes(text);
  const { rest: unbracketed, groups } = takeBrackets(segments.shift()!);
  let head = unbracketed.trim();
  const dashes = segments.map((s) => s.trim()).filter((s) => s !== "");

  const cues: string[] = [];
  const dashNotes: string[] = [];
  let rest: Rest | undefined = restWords !== null ? (restPair(restWords) ?? undefined) : undefined;
  if (restWords !== null && rest === undefined) dashNotes.push(`R: ${restWords}`);

  const withRest = /\s+with\s+(\d+(?:\.\d+)?\s*(?:s|sec|min|mins))\s+rest$/i.exec(head);
  if (withRest !== null && rest === undefined) {
    const single = restSingle(`${withRest[1]} rest`);
    if (single !== null) {
      rest = { reps: single };
      head = head.slice(0, withRest.index);
    }
  }

  let at: string | null = null;
  const atIndex = head.indexOf("@");
  if (atIndex >= 0) {
    at = head.slice(atIndex + 1).trim();
    head = head.slice(0, atIndex).trim();
  }
  const to = /\s+(to|at)\s+(\d+)(?:\s*-\s*(\d+))?\s*%$/i.exec(head);
  if (to !== null) {
    item.effort = { percent: count(to[2]!, to[3]), how: to[1]!.toLowerCase() === "to" ? "to" : "at" };
    head = head.slice(0, to.index).trim();
  }

  item.kind = kindOf(item, head, at);

  if (at !== null) {
    const percent = /^(\d+)(?:\s*-\s*(\d+))?\s*%(?:\s+of\s+(.+))?$/.exec(at);
    const hurdles = item.kind === "hurdles" ? hurdleAt(at) : null;
    if (percent !== null && item.effort === undefined) {
      item.effort = { percent: count(percent[1]!, percent[2]), how: "at" };
      if (percent[3] !== undefined) item.effort.of = percent[3].trim();
    } else if (hurdles !== null) {
      item.hurdles = hurdles;
    } else {
      head = `${head} @ ${at}`.trim();
    }
  }

  for (const dash of dashes) {
    const single = restSingle(dash);
    if (single !== null && rest === undefined) {
      rest = { reps: single };
      continue;
    }
    if (/^target is\s+/i.test(dash)) {
      item.target = dash.replace(/^target is\s+/i, "").trim();
      continue;
    }
    if (item.kind === "hurdles" && /^(very\s+)?low$/i.test(dash)) {
      (item.hurdles ??= {}).low = dash.toLowerCase() as "low" | "very low";
      continue;
    }
    dashNotes.push(dash);
  }

  const taken: string[] = [];
  for (const group of [...groups, ...restCues]) {
    const kept = bracketCue(item, group, rest);
    if (kept.rest !== undefined) rest = kept.rest;
    if (kept.taken !== undefined) taken.push(kept.taken);
    cues.push(...kept.cues);
  }

  if (item.kind === "run") {
    const lead = /^(very light|light|medium)\s+(?=\S)/i.exec(head);
    if (lead !== null && item.effort === undefined) {
      item.effort = { word: lead[1]!.toLowerCase() as EffortWord };
      head = head.slice(lead[0].length);
    }
    const split = runWords(head, [...cues, ...taken, ...dashNotes, ...(item.load !== undefined ? ["sled"] : [])]);
    item.run = split.parts;
    if (!options.keepRunWords) head = split.words;
  }

  item.name = head.replace(/\s+/g, " ").trim();
  if (rest !== undefined) item.rest = rest;
  if (cues.length > 0) item.cues = cues;
  if (dashNotes.length > 0) item.dashNotes = dashNotes;
  if (item.name === "" && item.amount === undefined) return null;
  return item;
}

function kindOf(item: Item, head: string, at: string | null): ItemKind {
  const unit = item.amount?.unit;
  if (unit === "H") return "hurdles";
  if (/^hurdles?\b/i.test(head) && at !== null && /discount|\d+\s*(in|feet|ft)\b/i.test(at)) return "hurdles";
  if (unit === "m" || item.amount?.parts !== undefined) {
    return /(?<![\w-])(push|pushes|carry|carries|lunges?|crawl|crawls|jumps?|hops?|throws?|walk|walks|bounds?|bounding)(?![\w-])/i.test(head) ? "drill" : "run";
  }
  if (unit === "min") return "cardio";
  if (unit === "laps") return "cardio";
  if (unit === "s") return "hold";
  if (/\b(throws?|jumps?|hops?|bounds?|bounding|pop-ups?|take-?offs?|approach(es)?|hurdles?|skips?|scissors?|flops?|SP|JT|DT|MB|drills?)\b/i.test(head)) return "drill";
  return "lift";
}

function bracketCue(item: Item, group: string, rest: Rest | undefined): { cues: string[]; rest?: Rest; taken?: string } {
  const text = group.trim();
  if (text === "") return { cues: [] };
  if (rest === undefined) {
    const single = restSingle(text);
    if (single !== null) return { cues: [], rest: { reps: single } };
    const pair = /^r\s*:\s*(.+)$/i.exec(text);
    if (pair !== null) {
      const r = restPair(pair[1]!);
      if (r !== null) return { cues: [], rest: r };
    }
  }
  // `(on grass)`: the run's surface field, so changing the field changes the line.
  if (item.kind === "run" && /^(?:on\s+)?(?:the\s+)?(?:grass|turf|track|sand)$/i.test(text)) return { cues: [], taken: text };
  if (item.kind === "run" || item.kind === "drill" || item.kind === "hurdles") {
    const load = loadIn(text);
    if (load !== null && item.load === undefined) {
      item.load = load;
      return { cues: [] };
    }
  }
  if (item.kind === "hurdles" && /^\d+(-\d+){1,}$/.test(text) && item.hurdles?.steps === undefined) {
    (item.hurdles ??= {}).steps = text;
    return { cues: [] };
  }
  if (item.effort === undefined) {
    const parts = splitComma(text);
    const word = effortWord(parts[0]!);
    if (word !== null) {
      item.effort = { word };
      return { cues: parts.length > 1 ? [parts.slice(1).join(", ")] : [] };
    }
  }
  return { cues: [text] };
}

function loadIn(text: string): Load | null {
  const sled = /^(light|medium|heavy)\s+sled$/i.exec(text);
  if (sled !== null) return { sled: sled[1]!.toLowerCase() as "light" | "medium" | "heavy" };
  // As the form writes it: `1080`, `1080 assisted`, `1080 @ 2.5kg`, `1080 @ 1.5-2.5kg`, `1080 decreasing 5,3,1`.
  const machine = /^1080(\s+assisted)?(?:\s*@\s*(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?\s*kg)?(?:\s+decreasing\s+(\d+(?:\.\d+)?(?:\s*,\s*\d+(?:\.\d+)?)+))?$/i.exec(text);
  if (machine === null) return null;
  const load: Load = { machine: "1080" };
  if (machine[1] !== undefined) load.assisted = true;
  if (machine[2] !== undefined) load.kg = count(machine[2], machine[3]);
  if (machine[4] !== undefined) load.decreasing = machine[4].split(",").map((n) => Number.parseFloat(n));
  return load;
}

function hurdleAt(at: string): Hurdles | null {
  const both = /^(.+?)\s+(with|and)\s+(.+?)\s+discount$/i.exec(at);
  if (both !== null) return { height: both[1]!.trim(), discount: both[3]!.trim(), ...(both[2]!.toLowerCase() === "and" ? { joiner: "and" as const } : {}) };
  const discount = /^([\d.,/-]+(?:\s*(?:feet|foot|ft))?)\s+discount$/i.exec(at);
  if (discount !== null) return { discount: discount[1]!.trim() };
  const height = /^(\d+(?:\.\d+)?(?:\/\d+(?:\.\d+)?)?\s*in)$/i.exec(at);
  if (height !== null) return { height: height[1]!.trim() };
  const spacing = /^(\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?\s*(?:feet|foot|ft))$/i.exec(at);
  if (spacing !== null) return { spacing: spacing[1]!.trim() };
  return null;
}

function effortWord(text: string): EffortWord | null {
  const t = text.trim().toLowerCase();
  return t === "very light" || t === "light" || t === "medium" || t === "heavy" || t === "very heavy" ? t : null;
}

/** `walk-back rest`, `full rest`, `Full rest`, `5min rest`, `3min break`, `45s rest`. */
function restSingle(text: string): RestValue | null {
  const t = text.trim();
  if (/^walk[\s-]?back(\s+rest)?$/i.test(t)) return { walkBack: true };
  if (/^full(\s+rest)?$/i.test(t)) return { full: true };
  const time = /^(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?\s*(min|mins|minutes?|s|sec|secs|seconds?)\s+(rest|break)$/i.exec(t);
  if (time !== null) {
    const minutes = /^m/i.test(time[3]!);
    return { seconds: scaled(count(time[1]!, time[2]), minutes ? 60 : 1), as: minutes ? "min" : "s" };
  }
  return null;
}

/** After `R:`: `1/5`, `90s/6min`, `1-2min/7min`, `walk-back/5min`, `2-3min`, `60s`, `2/10min`. */
function restPair(text: string): Rest | null {
  const t = text.trim();
  const sides = t.split("/");
  if (sides.length > 2) return null;
  const pair = sides.length === 2;
  const values = sides.map((side) => restValue(side, pair));
  if (values.some((v) => v === null)) return null;
  return pair ? { reps: values[0]!, sets: values[1]! } : { reps: values[0]! };
}

function restValue(text: string, bareIsMinutes: boolean): RestValue | null {
  const t = text.trim();
  if (/^walk[\s-]?back$/i.test(t)) return { walkBack: true };
  if (/^full$/i.test(t)) return { full: true };
  const m = /^(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?\s*(min|mins|s|sec|secs)?$/i.exec(t);
  if (m === null) return null;
  const unit = m[3];
  if (unit === undefined && !bareIsMinutes) return null;
  const minutes = unit === undefined || /^m/i.test(unit);
  return { seconds: scaled(count(m[1]!, m[2]), minutes ? 60 : 1), as: unit === undefined ? "bare" : minutes ? "min" : "s" };
}

// MARK: The count at the front

/**
 * A count typed on its own, as he writes one: `4x3`, `2x3x30m`, `6-8`, `30s`, `4x8H`, `3min`,
 * `2x3x(150m+30s+100)`, `2x` (sets, nothing counted in each). `{}` when empty, null when it isn't one.
 */
export function parseCount(text: string): Pick<Item, "rounds" | "sets" | "amount"> | null {
  const t = text.trim().replace(/×/g, "x");
  if (t === "") return {};
  const counted = leadingCount(t);
  if (counted === null || counted.rest.trim() !== "") return null;
  const { rounds, sets, amount } = counted.fields;
  return { ...(rounds ? { rounds } : {}), ...(sets ? { sets } : {}), ...(amount ? { amount } : {}) };
}

const unitWords: Record<string, AmountUnit> = {
  m: "m", s: "s", sec: "s", secs: "s", min: "min", mins: "min", H: "H",
};

/**
 * `5x3`, `2x3x30m`, `4x8H`, `20`, `6-8`, `4-6 x 30m`, `4 x 2-step …` (4 of them), `3min`,
 * `2x3x(150m+30s+100)`, `2x4x(6H @ 6-8 discount - very low)`, `5x (2 cleans from box)`.
 */
function leadingCount(input: string): { fields: Partial<Item>; rest: string } | null {
  let text = input;
  const numbers: { count: Count; unit?: AmountUnit }[] = [];
  let parts: { parts: number[]; between?: string } | null = null;
  let inner: string | null = null;
  let dangling = false;

  for (;;) {
    const num = /^(\d+(?:\.\d+)?)(?![\d.])(?:\s*-\s*(\d+(?:\.\d+)?)(?![\da-wyz_-]))?(?!-[a-z])/i.exec(text);
    const group = /^\(([^()]*(?:\([^()]*\)[^()]*)*)\)/.exec(text);
    if (numbers.length > 0 && dangling && group !== null) {
      const p = partsOf(group[1]!);
      if (p !== null) parts = p;
      else inner = group[1]!;
      text = text.slice(group[0].length).trim();
      dangling = false;
      break;
    }
    if (num === null) break;
    let after = text.slice(num[0].length);
    let unit: AmountUnit | undefined;
    const u = /^\s?(m|s|sec|secs|min|mins|H)(?![a-zA-Z])/.exec(after);
    if (u !== null && (u[1] !== "H" || /^H/.test(after))) {
      unit = unitWords[u[1]!];
      after = after.slice(u[0].length);
    }
    numbers.push({ count: count(num[1]!, num[2]), ...(unit !== undefined ? { unit } : {}) });
    const x = /^\s*x\s*/i.exec(after);
    if (x !== null && unit === undefined && numbers.length < 3) {
      text = after.slice(x[0].length);
      dangling = true;
      continue;
    }
    text = after;
    dangling = false;
    break;
  }
  if (numbers.length === 0) return null;
  text = text.trim();
  // `2x Hanging knee raises`, `4 x flying 4-step full jumps`: a count of sets and nothing per set.
  if (dangling && inner === null && parts === null) {
    const counts = numbers.map((n) => n.count);
    const fields: Partial<Item> = counts.length === 2 ? { rounds: counts[0]!, sets: counts[1]! } : { sets: counts[0]! };
    return { fields, rest: text };
  }

  const fields: Partial<Item> = {};
  if (inner !== null) {
    const innerItem = leadingCount(inner.trim());
    if (innerItem === null || innerItem.fields.amount === undefined) return null;
    const outer = numbers.map((n) => n.count);
    const innerCounts = [innerItem.fields.rounds, innerItem.fields.sets].filter((c): c is Count => c !== undefined);
    const all = [...outer, ...innerCounts];
    if (all.length > 2) return null;
    if (all.length === 2) {
      fields.rounds = all[0]!;
      fields.sets = all[1]!;
    } else {
      fields.sets = all[0]!;
    }
    fields.amount = { ...innerItem.fields.amount, bracketed: true };
    return { fields, rest: `${innerItem.rest} ${text}`.trim() };
  }
  if (parts !== null) {
    const amount: Amount = { n: parts.parts[0]!, unit: "m", parts: parts.parts };
    if (parts.between !== undefined) amount.between = parts.between;
    if (numbers.length === 2) {
      fields.rounds = numbers[0]!.count;
      fields.sets = numbers[1]!.count;
    } else {
      fields.sets = numbers[0]!.count;
    }
    fields.amount = amount;
    return { fields, rest: text };
  }

  const last = numbers[numbers.length - 1]!;
  const amount: Amount = { ...last.count, unit: last.unit ?? "reps" };
  if (numbers.length === 3) {
    fields.rounds = numbers[0]!.count;
    fields.sets = numbers[1]!.count;
  } else if (numbers.length === 2) {
    fields.sets = numbers[0]!.count;
  }
  fields.amount = amount;
  return { fields, rest: text };
}

/** `150m+30s+100`: the runs, and what is between them. */
function partsOf(text: string): { parts: number[]; between?: string } | null {
  const pieces = text.split("+").map((p) => p.trim());
  if (pieces.length < 2 || pieces.some((p) => !/^\d+(?:\.\d+)?\s*(m|s|sec|min)?$/i.test(p))) return null;
  const runs: number[] = [];
  let between: string | undefined;
  for (const piece of pieces) {
    if (/(s|sec|min)$/i.test(piece)) between = piece.replace(/\s+/g, "");
    else runs.push(Number.parseFloat(piece));
  }
  if (runs.length < 2) return null;
  return between !== undefined ? { parts: runs, between } : { parts: runs };
}

// MARK: Text

function count(n: string, upper?: string): Count {
  const value = Number.parseFloat(n);
  const top = upper !== undefined ? Number.parseFloat(upper) : undefined;
  return top !== undefined && top !== value ? { n: value, upper: top } : { n: value };
}

function scaled(c: Count, by: number): Count {
  return c.upper !== undefined ? { n: c.n * by, upper: c.upper * by } : { n: c.n * by };
}

/** Every top-level `(…)` taken out, in order; nested brackets stay inside their group. */
function takeBrackets(text: string): { rest: string; groups: string[] } {
  const groups: string[] = [];
  let rest = "";
  let depth = 0;
  let current = "";
  for (const c of text) {
    if (c === "(") {
      if (depth > 0) current += c;
      depth += 1;
      continue;
    }
    if (c === ")" && depth > 0) {
      depth -= 1;
      if (depth === 0) {
        groups.push(current.trim());
        current = "";
        rest += " ";
      } else current += c;
      continue;
    }
    if (depth > 0) current += c;
    else rest += c;
  }
  if (depth > 0 && current.trim() !== "") groups.push(current.trim());
  return { rest: rest.replace(/\s+/g, " ").replace(/\s+([,.;:])/g, "$1").trim(), groups };
}

function withoutBrackets(text: string): string {
  return takeBrackets(text).rest;
}

function starsOutside(text: string): string {
  let out = "";
  let depth = 0;
  for (const c of text) {
    if (c === "(") depth += 1;
    if (c === ")") depth = Math.max(0, depth - 1);
    if (c === "*" && depth === 0) continue;
    out += c;
  }
  return out.replace(/\s+/g, " ").trim();
}

/** Split at ` - ` (or an en or em dash) outside brackets. */
function splitTopDashes(text: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let current = "";
  const cs = [...text];
  for (let i = 0; i < cs.length; i++) {
    const c = cs[i]!;
    if (c === "(") depth += 1;
    if (c === ")") depth = Math.max(0, depth - 1);
    if (depth === 0 && (c === "-" || c === "–" || c === "—") && cs[i - 1] === " " && cs[i + 1] === " ") {
      out.push(current.trim());
      current = "";
      i += 1;
      continue;
    }
    current += c;
  }
  out.push(current.trim());
  return out;
}

function splitTop(text: string, sep: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let current = "";
  for (const c of text) {
    if (c === "(") depth += 1;
    if (c === ")") depth = Math.max(0, depth - 1);
    if (c === sep && depth === 0) {
      out.push(current);
      current = "";
      continue;
    }
    current += c;
  }
  out.push(current);
  return out;
}

function splitComma(text: string): string[] {
  return splitTop(text, ",").map((p) => p.trim()).filter((p) => p !== "");
}
